/**
 * Системные оповещения.
 *
 * Одна основа на оболочку и веб: web-API `Notification`. В Electron окно
 * renderer показывает им настоящее системное уведомление, в браузере — его
 * же; разводить правила «когда показывать» по двум местам вызова незачем.
 *
 * Разрешение спрашивается один раз и только по действию человека — включением
 * переключателя в настройках. Просить его на первом же входящем сообщении
 * значит спросить в тот момент, когда человек занят чем-то другим, и получить
 * отказ навсегда.
 */

import { useStreamer } from "@shared/ui/streamer";
import { windowControls } from "@shared/lib/desktop";

const KEY = "airel.notify";

/** Просил ли человек оповещения. Хранится здесь же — это выбор устройства. */
export function wanted(): boolean {
  return localStorage.getItem(KEY) === "1";
}

export function setWanted(value: boolean) {
  localStorage.setItem(KEY, value ? "1" : "0");
}

/** Умеет ли эта сборка показывать оповещения вообще. */
export function supported(): boolean {
  return typeof Notification !== "undefined";
}

/**
 * Спрашивает разрешение и отвечает, дали ли его.
 *
 * Отказ не запоминается нами: его помнит система, и второй раз она спросит
 * сама, если человек передумает в её настройках.
 */
export async function allow(): Promise<boolean> {
  if (typeof Notification === "undefined") return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;

  return (await Notification.requestPermission()) === "granted";
}

/** Почему оповещение не показалось. `null` — показалось. */
export type Refusal = "off" | "unsupported" | "denied" | "failed";

/**
 * Показывает оповещение и отвечает, что помешало.
 *
 * Ответ нужен не обработчику сообщений — тому разбираться всё равно негде, —
 * а проверке в настройках: без него «ничего не появилось» означает сразу и
 * «выключено», и «система не пустила», и «сломалось», а чинятся они по-разному.
 */
export async function show(
  title: string,
  body: string,
  onClick?: () => void,
): Promise<Refusal | null> {
  try {
    if (typeof Notification === "undefined") return "unsupported";
    if (Notification.permission !== "granted") return "denied";

    // Метка одна на отправителя: десять сообщений подряд не должны выстроить
    // десять окошек. Система заменяет предыдущее тем же тегом.
    const note = new Notification(title, { body, tag: title });
    // Нажали — как в Discord: окно вперёд и туда, о чём оповещение. В
    // оболочке окно может быть спрятано в трей — `focus` его не покажет.
    note.onclick = () => {
      void windowControls.show();
      window.focus();
      onClick?.();
      note.close();
    };
    return null;
  } catch {
    return "failed";
  }
}

/**
 * Оповещение о новом сообщении.
 *
 * Молча ничего не делает, если выключено или не разрешено: место вызова —
 * обработчик события, и разбираться там, почему не вышло, негде.
 */
export async function notify(title: string, body: string, onClick?: () => void) {
  // В режиме стримера оповещения не всплывают — их видно на трансляции.
  if (!wanted() || useStreamer.getState().on) return;
  await show(title, body, onClick);
}
